/**
 * Skio Build-a-Box cart helpers
 * Based on: https://github.com/skio-org/bab-boilerplate-theme-code
 *
 * Classic boxes: _Skio_box_index / _Skio_box_parent_index (boilerplate)
 * Dynamic boxes: _dynamicBoxId / _dynamicBoxIndex
 * https://help.skio.com/docs/dynamic-box-api-guide-for-developers
 */

let skioDynamicBoxRemovalInProgress = false;
let skioCartCheckInFlight = false;
let skioCartCheckQueued = false;
let skioCartDebounceTimer = null;
let skioLastCartSignature = null;

const skioMoneyFormatter = new Intl.NumberFormat(
  document.documentElement.lang?.replace('_', '-') || 'en-US',
  {
    style: 'currency',
    currency: window?.Shopify?.currency?.active || 'USD'
  }
);

document.addEventListener('DOMContentLoaded', function () {
  if (skioCartIsPresent()) {
    skioQueueCheckCart();
    observeCartForSkioUpdates();
  }
});

document.addEventListener('cart:update', skioQueueCheckCart);
document.addEventListener('cart:updated', skioQueueCheckCart);
document.addEventListener('cart:refresh', skioQueueCheckCart);

const SKIO_CART_BADGES = {
  addon: 'Add-on'
};

function skioCartIsPresent() {
  return Boolean(
    document.getElementById('main-cart-items') ||
      document.querySelector('cart-items') ||
      document.querySelector('[id^="MiniCart-"]') ||
      document.querySelector('[id^="MainCart-"]')
  );
}

function skioInvalidateCartCache() {
  skioLastCartSignature = null;
}

function skioQueueCheckCart() {
  if (skioCartDebounceTimer) {
    clearTimeout(skioCartDebounceTimer);
  }

  skioCartDebounceTimer = setTimeout(() => {
    skioCartDebounceTimer = null;
    skioRunCheckCart();
  }, 100);
}

function buildCartSignature(cart) {
  return JSON.stringify(
    cart.items.map((item) => ({
      key: item.key,
      quantity: item.quantity,
      final_line_price: item.final_line_price,
      dynamicBoxId: item.properties?._dynamicBoxId ?? null,
      dynamicBoxIndex: item.properties?._dynamicBoxIndex ?? null,
      dynamicBoxUpsell: item.properties?._dynamicBoxUpsell ?? null
    }))
  );
}

function skioCartNeedsApply(cart) {
  const signature = buildCartSignature(cart);
  if (signature !== skioLastCartSignature) {
    return true;
  }

  const hasDynamicBox = cart.items.some(
    (item) => item.properties?._dynamicBoxId && !item.properties?._dynamicBoxUpsell
  );

  if (!hasDynamicBox) {
    return document.querySelector('.skio-box-group-header') !== null;
  }

  return document.querySelector('.skio-box-group-header') === null;
}

function observeCartForSkioUpdates() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;

  cartDrawer.addEventListener('drawer:afterShow', () => {
    skioInvalidateCartCache();
    skioQueueCheckCart();
  });
}

function formatSkioMoney(cents) {
  const settings = window.theme?.settings || {};
  const formatString = settings.currencyCodeEnabled
    ? settings.moneyWithCurrencyFormat
    : settings.moneyFormat;

  if (typeof window.theme?.Currency?.formatMoney === 'function' && formatString) {
    return window.theme.Currency.formatMoney(cents, formatString);
  }

  return skioMoneyFormatter.format(cents / 100);
}

function getSkioRemoveLabel(referenceEl) {
  const link = referenceEl?.querySelector('[is="cart-remove-button"]');
  return link?.textContent?.trim() || 'Remove';
}

function createSkioBoxHeaderElement(tagName) {
  const header = document.createElement(tagName === 'TR' ? 'tr' : 'li');
  header.className = 'skio-box-group-header';
  header.dataset.skioDynamicBox = 'true';

  const innerMarkup = `
    <div class="skio-box-group-header__inner flex flex-wrap items-center justify-between gap-2 w-full">
      <div class="skio-box-group-header__lead flex flex-wrap items-center">
        <span class="badge badge--highlight font-medium leading-none">Your Box</span>
        <div class="price text-sm flex flex-wrap gap-1d5">
          <span class="skio-box-group-header__price whitespace-nowrap"></span>
        </div>
      </div>
      <div class="text-xs text-right relative skio-box-group-header__remove-wrap">
        <a class="link" href="#" data-skio-box-remove data-no-instant></a>
      </div>
    </div>
  `;

  if (tagName === 'TR') {
    header.innerHTML = `<td colspan="3">${innerMarkup}</td>`;
  } else {
    header.innerHTML = innerMarkup;
  }

  return header;
}

function renderSkioDynamicBoxHeader(boxNumber, group) {
  const firstItem = group.items[0];
  if (!firstItem) return;

  const formattedPrice = formatSkioMoney(group.total);
  const placements = [
    {
      firstId: `CartDrawer-Item-${firstItem.key}`,
      headerId: `skio-box-header-drawer-${boxNumber}`
    },
    {
      firstId: `CartItem-${firstItem.key}`,
      headerId: `skio-box-header-cart-${boxNumber}`
    }
  ];

  placements.forEach(({ firstId, headerId }) => {
    const firstEl = document.getElementById(firstId);
    if (!firstEl?.parentElement) return;

    const list = firstEl.parentElement;
    let header = document.getElementById(headerId);

    if (!header) {
      header = createSkioBoxHeaderElement(firstEl.tagName);
      header.id = headerId;
      header.dataset.skioBoxNumber = String(boxNumber);
      list.insertBefore(header, firstEl);
    }

    const priceEl = header.querySelector('.skio-box-group-header__price');
    if (priceEl) {
      priceEl.textContent = formattedPrice;
    }

    const removeEl = header.querySelector('[data-skio-box-remove]');
    if (removeEl) {
      removeEl.setAttribute('data-index', firstItem.key);
      const referenceRemove = firstEl.querySelector('[is="cart-remove-button"]');
      if (referenceRemove?.getAttribute('href')) {
        removeEl.setAttribute('href', referenceRemove.getAttribute('href'));
      }
      if (!removeEl.textContent.trim()) {
        removeEl.textContent = getSkioRemoveLabel(firstEl);
      }
    }
  });
}

function pruneSkioBoxHeaders(activeBoxNumbers) {
  const activeIds = new Set();
  activeBoxNumbers.forEach((boxNumber) => {
    activeIds.add(`skio-box-header-drawer-${boxNumber}`);
    activeIds.add(`skio-box-header-cart-${boxNumber}`);
  });

  document.querySelectorAll('.skio-box-group-header').forEach((header) => {
    if (!activeIds.has(header.id)) {
      header.remove();
    }
  });
}

function clearSkioBoxLineClasses() {
  document.querySelectorAll('.skio-box, .skio-cart-line--addon').forEach((el) => {
    el.classList.remove(
      'skio-box',
      'skio-box--child',
      'skio-box--hidden-price',
      'skio-cart-line--box-parent',
      'skio-cart-line--box-child',
      'skio-cart-line--addon'
    );
    delete el.dataset.skioDynamicBox;
    delete el.dataset.skioDynamicBoxUpsell;
    for (let i = 1; i <= 12; i += 1) {
      el.classList.remove(`skio-box-${i}`);
    }
    el.querySelectorAll('.skio-cart-line-badge').forEach((badge) => badge.remove());
  });
}

function decorateSkioCartLine(el, role) {
  if (!el) return;

  el.classList.remove(
    'skio-cart-line--box-parent',
    'skio-cart-line--box-child',
    'skio-cart-line--addon'
  );

  const roleClassMap = {
    boxParent: 'skio-cart-line--box-parent',
    boxChild: 'skio-cart-line--box-child',
    addon: 'skio-cart-line--addon'
  };

  const roleClass = roleClassMap[role];
  const label = SKIO_CART_BADGES[role];

  if (roleClass) {
    el.classList.add(roleClass);
  }

  const existingBadge = el.querySelector('.skio-cart-line-badge');
  if (!label) {
    existingBadge?.remove();
    return;
  }

  let badge = existingBadge;
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'badge badge--highlight font-medium leading-none skio-cart-line-badge';
    const title = el.querySelector('.horizontal-product__title, .cart__item-title');
    const titleWrap = title?.parentElement;
    if (titleWrap) {
      titleWrap.insertBefore(badge, title);
    } else {
      const details = el.querySelector(
        '.horizontal-product__details, .cart-item__details'
      );
      details?.prepend(badge);
    }
  }

  badge.textContent = label;
}

function updateSkioBoxTotalPrice(boxNumber, totalCents) {
  if (totalCents <= 0) return;

  const formattedPrice = formatSkioMoney(totalCents);

  document
    .querySelectorAll(
      `#skio-box-header-drawer-${boxNumber} .skio-box-group-header__price, #skio-box-header-cart-${boxNumber} .skio-box-group-header__price`
    )
    .forEach((el) => {
      el.textContent = formattedPrice;
    });

  const selectors = [
    `.cart-item.skio-box.skio-box-${boxNumber} .cart-item__price-wrapper .price`,
    `.cart-item.skio-box.skio-box-${boxNumber} .product-option.price`,
    `.horizontal-product.skio-box.skio-box-${boxNumber} .price`
  ];

  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (el.closest('.skio-box--child')) return;
      if (el.closest('.skio-box-group-header')) return;
      if (el.textContent.trim()) {
        el.textContent = formattedPrice;
      }
    });
  });
}

function applySkioBoxLineClasses(item, boxNumber, isChild, { isDynamic = false } = {}) {
  const drawerEl = document.getElementById(`CartDrawer-Item-${item.key}`);
  const cartRow = document.getElementById(`CartItem-${item.key}`);

  [drawerEl, cartRow].forEach((el) => {
    if (!el) return;
    el.classList.add('cart-item', 'skio-box', `skio-box-${boxNumber}`);
    if (isDynamic) {
      el.dataset.skioDynamicBox = 'true';
    } else {
      delete el.dataset.skioDynamicBox;
    }
    if (isChild) {
      el.classList.add('skio-box--child', 'skio-box--hidden-price');
    }
  });
}

function processDynamicBoxUpsells(cart) {
  cart.items.forEach((item) => {
    const isUpsell =
      item.properties?.['_dynamicBoxUpsell'] === 'true' ||
      item.properties?.['_dynamicBoxUpsell'] === true;
    if (!isUpsell) return;

    const drawerEl = document.getElementById(`CartDrawer-Item-${item.key}`);
    const cartRow = document.getElementById(`CartItem-${item.key}`);

    [drawerEl, cartRow].forEach((el) => {
      if (!el) return;
      el.dataset.skioDynamicBoxUpsell = 'true';
      el.classList.add('skio-cart-line--addon');
      decorateSkioCartLine(el, 'addon');
    });
  });
}

function processDynamicBoxes(cart) {
  const groups = {};
  const activeBoxNumbers = [];

  cart.items.forEach((item) => {
    const boxId = item.properties?.['_dynamicBoxId'];
    if (!boxId || item.properties?.['_dynamicBoxUpsell']) return;

    const boxIndex = parseInt(item.properties?.['_dynamicBoxIndex'], 10) || 0;
    const groupKey = `${boxId}:${boxIndex}`;

    if (!groups[groupKey]) {
      groups[groupKey] = { items: [], total: 0 };
    }

    groups[groupKey].items.push(item);
    groups[groupKey].total += item.final_line_price;
  });

  let boxNumber = 0;
  Object.values(groups).forEach((group) => {
    boxNumber += 1;
    activeBoxNumbers.push(boxNumber);
    group.items.forEach((item) => {
      applySkioBoxLineClasses(item, boxNumber, true, { isDynamic: true });
    });
    renderSkioDynamicBoxHeader(boxNumber, group);
    updateSkioBoxTotalPrice(boxNumber, group.total);
  });

  pruneSkioBoxHeaders(activeBoxNumbers);
}

function applySkioCartFromData(cart) {
  clearSkioBoxLineClasses();
  processDynamicBoxes(cart);
  processDynamicBoxUpsells(cart);

  const boxInfo = { indexes: [] };

  cart.items.forEach((item) => {
    if (item.properties['_Skio_box_parent_index'] !== undefined) {
      const parentIndex = item.properties['_Skio_box_parent_index'];
      if (boxInfo[parentIndex] === undefined) {
        boxInfo[parentIndex] = [];
      }
      boxInfo[parentIndex].push(item);
    }
    if (item.properties['_Skio_box_index'] !== undefined) {
      boxInfo.indexes.push(item.properties['_Skio_box_index']);
    }
  });

  let remove;
  for (const [key, value] of Object.entries(boxInfo)) {
    if (key === 'indexes') continue;

    if (!boxInfo.indexes.includes(parseInt(key, 10))) {
      remove = value;
      continue;
    }

    let total = 0;
    value.forEach((item) => {
      total += item.final_line_price;
    });

    const boxNumber = parseInt(key, 10) + 1;
    value.forEach((item, index) => {
      applySkioBoxLineClasses(item, boxNumber, index > 0);
    });

    updateSkioBoxTotalPrice(boxNumber, total);
  }

  if (remove) {
    const updates = {};
    remove.forEach((item) => {
      updates[item.key] = 0;
    });

    skioCartUpdate(updates).then((response) => {
      skioRefreshCartUi(response);
    });
  }
}

function skioRunCheckCart() {
  if (skioCartCheckInFlight) {
    skioCartCheckQueued = true;
    return;
  }

  skioCartCheckInFlight = true;

  fetch('/cart.js')
    .then((response) => response.json())
    .then((cart) => {
      if (!skioCartNeedsApply(cart)) {
        return;
      }

      skioLastCartSignature = buildCartSignature(cart);
      applySkioCartFromData(cart);
    })
    .catch((error) => {
      console.log('[skio] error fetching cart:', error);
    })
    .finally(() => {
      skioCartCheckInFlight = false;

      if (skioCartCheckQueued) {
        skioCartCheckQueued = false;
        skioQueueCheckCart();
      }
    });
}

function getDynamicBoxUpdatesForItem(cart, itemKey) {
  const item = cart.items.find((line) => line.key === itemKey);
  if (!item?.properties?._dynamicBoxId || item.properties._dynamicBoxUpsell) {
    return null;
  }

  const boxId = String(item.properties._dynamicBoxId);
  const boxIndex = parseInt(item.properties._dynamicBoxIndex, 10) || 0;
  const updates = {};

  cart.items.forEach((line) => {
    if (
      String(line.properties?._dynamicBoxId) === boxId &&
      (parseInt(line.properties?._dynamicBoxIndex, 10) || 0) === boxIndex &&
      !line.properties?._dynamicBoxUpsell
    ) {
      updates[line.key] = 0;
    }
  });

  return Object.keys(updates).length > 0 ? updates : null;
}

function collectBundledSectionIds() {
  const sectionsToBundle = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    })
  );
  return sectionsToBundle;
}

function skioCartUpdate(updates) {
  const sectionIds = collectBundledSectionIds();
  const url = sectionIds.length
    ? `/cart/update.js?sections=${sectionIds.join(',')}`
    : '/cart/update.js';

  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates })
  }).then((res) => res.json());
}

async function skioRefreshCartUi(response) {
  const cartDrawer = document.querySelector('cart-drawer');
  const mainCart = document.querySelector('[id^="MainCart-"]');
  const rootUrl = window.theme?.routes?.root_url || '/';

  skioInvalidateCartCache();

  if (cartDrawer && typeof cartDrawer.onCartRefresh === 'function') {
    await cartDrawer.onCartRefresh({ detail: { open: true } });
    skioQueueCheckCart();
    return;
  }

  if (mainCart) {
    const sectionId = mainCart.getAttribute('data-section-id');
    if (sectionId) {
      const responseText = await fetch(`${rootUrl}?section_id=${sectionId}`).then(
        (res) => res.text()
      );
      const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
      const updated = parsedHTML.getElementById(mainCart.id);
      if (updated) {
        mainCart.innerHTML = updated.innerHTML;
        skioQueueCheckCart();
        return;
      }
    }
  }

  document.dispatchEvent(
    new CustomEvent('cart:refresh', { detail: { open: false } })
  );

  if (response?.items?.length > 0) {
    skioUpdateSections(response);
  } else {
    window.location.reload();
  }
}

document.addEventListener(
  'click',
  function (event) {
    const removeBtn = event.target.closest('[data-skio-box-remove]');
    if (!removeBtn || skioDynamicBoxRemovalInProgress) return;

    const header = removeBtn.closest('.skio-box-group-header');
    if (!header || header.dataset.skioDynamicBox !== 'true') return;

    const itemKey = removeBtn.getAttribute('data-index');
    if (!itemKey) return;

    event.preventDefault();
    event.stopPropagation();
    skioRemoveEntireDynamicBox(itemKey);
  },
  false
);

async function skioRemoveEntireDynamicBox(itemKey) {
  if (skioDynamicBoxRemovalInProgress) return false;

  try {
    const cart = await fetch('/cart.js').then((response) => response.json());
    const updates = getDynamicBoxUpdatesForItem(cart, itemKey);
    if (!updates) return false;

    skioDynamicBoxRemovalInProgress = true;
    const response = await skioCartUpdate(updates);
    skioRefreshCartUi(response);
    return true;
  } catch (error) {
    console.log('[skio] error removing dynamic box:', error);
    return false;
  } finally {
    skioDynamicBoxRemovalInProgress = false;
  }
}

function getSectionsToRender() {
  const sections = [];

  const mainCartItems = document.getElementById('main-cart-items');
  if (mainCartItems?.dataset?.id) {
    sections.push({
      id: 'main-cart-items',
      section: mainCartItems.dataset.id,
      selector: '.js-contents'
    });
    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section'
    });
    sections.push({
      id: 'cart-live-region-text',
      section: 'cart-live-region-text',
      selector: '.shopify-section'
    });
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter?.dataset?.id) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents'
      });
    }
  }

  return sections;
}

function getSectionInnerHTML(html, selector) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const node = parsed.querySelector(selector);
  return node ? node.innerHTML : '';
}

function skioUpdateSections(response) {
  skioInvalidateCartCache();

  getSectionsToRender().forEach((section) => {
    const root = document.getElementById(section.id);
    if (!root || !response.sections?.[section.section]) return;

    const elementToReplace =
      root.querySelector(section.selector) || root;
    elementToReplace.innerHTML = getSectionInnerHTML(
      response.sections[section.section],
      section.selector
    );
  });

  skioQueueCheckCart();
}

window.skioCheckCart = function skioCheckCart() {
  skioInvalidateCartCache();
  skioQueueCheckCart();
};
window.skioRemoveEntireDynamicBox = skioRemoveEntireDynamicBox;
